import { ClockIcon, PayrollTagIcon } from "./DashboardIcons";

import { DashboardCard } from "./DashboardCard";
import { PayrollProcessingData } from "../dashboardOverview.types";
import { formatCurrency } from "../dashboardFormat.utils";

export interface PayrollProcessingCardProps {
	data: PayrollProcessingData;
	onReviewApprove?: () => void;
}

// ===== CHANGED: matches the finalized reference — current step is a
// teal-ringed clock icon (not a solid navy circle), upcoming steps show
// their own step number in a light grey circle (not a plain dot) (copy
// this whole component) =====
const StepCircle = ({ status, stepNumber }: { status: PayrollProcessingData["steps"][number]["status"]; stepNumber: number }) => {
	if (status === "complete") {
		return (
			<div className="cd-step__circle cd-step__circle--complete">
				<svg width="13" height="13" viewBox="0 0 24 24" fill="none">
					<path d="M5 13l4 4 10-10" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</div>
		);
	}
	if (status === "current") {
		return (
			<div className="cd-step__circle cd-step__circle--current">
				<ClockIcon width={13} height={13} />
			</div>
		);
	}
	return (
		<div className="cd-step__circle cd-step__circle--upcoming">
			<span className="cd-step__number">{stepNumber}</span>
		</div>
	);
};

// ===== CHANGED: the connecting line is teal for any segment leaving a
// completed step (whether the next step is also complete or is the
// current one), and grey otherwise — matches the reference's continuous
// teal line up through the current step (copy this whole function) =====
const lineClassBetween = (current: PayrollProcessingData["steps"][number]["status"]): string => {
	return current === "complete" ? "cd-step-tracker__line--done" : "";
};

export const PayrollProcessingCard = ({ data, onReviewApprove }: PayrollProcessingCardProps) => {
	return (
		<DashboardCard testId="payroll-processing-card">
			<div className="cd-card__header">
				<div className="cd-card__title">
					<PayrollTagIcon />
					<h2>Payroll in progress</h2>
					{/* ===== CHANGED: outline pill (white bg, thin border) instead of the
						    filled grey neutral badge (copy this line) ===== */}
					<span className="cd-badge cd-badge--outline cd-payroll-run-badge">{data.runLabel}</span>
				</div>
				{/* ===== CHANGED: dark green button (not teal) with a trailing arrow
					    icon, matching image1 exactly (copy this button) ===== */}
				<button type="button" className="cd-review-approve-button" onClick={onReviewApprove}>
					Review &amp; approve
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
						<path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>
			</div>
			<p className="cd-card__subtitle">
				{data.payPeriodLabel} · {data.employeeCount} employees · {data.paydayLabel}
			</p>

			<div className="cd-step-tracker">
				{data.steps.map((step, index) => (
					<div className="cd-step-tracker__item" key={step.key}>
						<div className="cd-step">
							<StepCircle status={step.status} stepNumber={index + 1} />
							<span className={`cd-step__label${step.status === "current" ? " cd-step__label--current" : ""}`}>
								{step.label}
							</span>
							{step.dateLabel && <span className="cd-step__date">{step.dateLabel}</span>}
						</div>
						{index < data.steps.length - 1 && (
							<div className={`cd-step-tracker__line ${lineClassBetween(step.status)}`} />
						)}
					</div>
				))}
			</div>

			<div className="cd-payroll-summary cd-payroll-summary--figures-only">
				<div className="cd-payroll-summary__figures">
					<div>
						<span className="cd-figure__label">Gross Pay</span>
						<span className="cd-figure__value">{formatCurrency(data.grossPay, data.currency)}</span>
					</div>
					<div>
						<span className="cd-figure__label">Deductions &amp; Taxes</span>
						<span className="cd-figure__value">{formatCurrency(data.deductionsAndTaxes, data.currency)}</span>
					</div>
					<div>
						<span className="cd-figure__label">Net Pay</span>
						<span className="cd-figure__value">{formatCurrency(data.netPay, data.currency)}</span>
					</div>
				</div>
			</div>
		</DashboardCard>
	);
};